import { useState } from 'react'

import { FileImportPanel } from '@/components/jobs/FileImportPanel'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, SectionHeader } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/Checkbox'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Field, Input, Textarea } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Tabs, TabPanel, type TabItem } from '@/components/ui/Tabs'
import { Toggle } from '@/components/ui/Toggle'
import {
  useCapabilities,
  useCreateBrowserSearch,
  useCreateCurlCapture,
  useCreateJsonImport,
  useImportableFiles,
} from '@/hooks/useJobs'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/cn'
import { presentError } from '@/lib/errors'
import { formatBytes, formatNumber, formatRelative } from '@/lib/format'

type Mode = 'import' | 'curl' | 'browser'

const UploadGlyph = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
    <path
      d="M8 10.5V3M5.5 5.5L8 3l2.5 2.5M3 11v1.5A1.5 1.5 0 0 0 4.5 14h7a1.5 1.5 0 0 0 1.5-1.5V11"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const TerminalGlyph = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
    <path
      d="M3.5 5.5L6 8l-2.5 2.5M8.5 11h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
  </svg>
)
const SearchGlyph = () => (
  <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
    <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

const MODES: Array<TabItem<Mode> & { hint: string }> = [
  {
    id: 'import',
    label: 'Import files',
    icon: <UploadGlyph />,
    hint: 'Upload a CSV, JSON, TSV or Excel file, or pick one already on the server.',
  },
  {
    id: 'curl',
    label: 'Paste cURL',
    icon: <TerminalGlyph />,
    hint: 'Replay a search you already ran in your own browser. No browser needed here.',
  },
  {
    id: 'browser',
    label: 'Live search',
    icon: <SearchGlyph />,
    hint: 'Drive the attached Chrome against the portal and walk every page of results.',
  },
]

const STATUS_OPTIONS = ['ALL', 'OPEN', 'CLOSED', 'AMENDED', 'REVIVED', 'VOIDED'].map(
  (value) => ({ value, label: value === 'ALL' ? 'All statuses' : value }),
)
const ROLE_OPTIONS = ['ALL', 'DEBTOR', 'CREDITOR'].map((value) => ({
  value,
  label: value === 'ALL' ? 'Either role' : value,
}))

/** The ways records get into the database, filtered to what the engine supports. */
export function NewJobPanel() {
  const [mode, setMode] = useState<Mode>('import')
  const capabilities = useCapabilities()

  // A route the engine cannot perform is not offered at all, rather than shown
  // as a button that always fails. Until the probe answers, assume everything
  // is available so the panel does not flicker.
  const enabledModes = MODES.filter((item) => {
    if (!capabilities.data) return true
    if (item.id === 'curl') return capabilities.data.curlCapture
    if (item.id === 'browser') return capabilities.data.browserSearch
    return capabilities.data.jsonImport
  })

  // If the selected tab disappears once capabilities arrive, fall back.
  const activeMode = enabledModes.some((item) => item.id === mode)
    ? mode
    : (enabledModes[0]?.id ?? 'import')
  const { push } = useToast()

  const importFiles = useImportableFiles()
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const [confirmServerImport, setConfirmServerImport] = useState(false)

  const [curlCommand, setCurlCommand] = useState('')
  const [curlLabel, setCurlLabel] = useState('')

  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [status, setStatus] = useState('ALL')
  const [partyRole, setPartyRole] = useState('ALL')

  const [fetchDetails, setFetchDetails] = useState(false)

  const jsonImport = useCreateJsonImport()
  const curlCapture = useCreateCurlCapture()
  const browserSearch = useCreateBrowserSearch()

  const onQueued = (message: string) => {
    push({ tone: 'success', title: 'Job queued', description: message })
  }
  const onFailed = (error: unknown) => {
    const presented = presentError(error, 'Could not start the job')
    push({ tone: 'error', title: presented.title, description: presented.message })
  }

  const serverFiles = importFiles.data ?? []
  const chosenCount = selectedPaths.length || serverFiles.length

  const submitImport = () => {
    setConfirmServerImport(false)
    jsonImport.mutate(selectedPaths, {
      onSuccess: (result) => {
        onQueued(result.message)
        setSelectedPaths([])
      },
      onError: onFailed,
    })
  }

  const submitCurl = () => {
    curlCapture.mutate(
      { curlCommand, label: curlLabel, fetchDetails, maxPages: 500 },
      {
        onSuccess: (result) => {
          onQueued(result.message)
          setCurlCommand('')
          setCurlLabel('')
        },
        onError: onFailed,
      },
    )
  }

  const submitBrowser = () => {
    browserSearch.mutate(
      {
        lastName,
        firstName,
        status,
        partyRole,
        location: 'ALL',
        fetchDetails,
        maxPages: 500,
      },
      { onSuccess: (result) => onQueued(result.message), onError: onFailed },
    )
  }

  const togglePath = (path: string) => {
    setSelectedPaths((current) =>
      current.includes(path)
        ? current.filter((entry) => entry !== path)
        : [...current, path],
    )
  }

  const activeHint = enabledModes.find((entry) => entry.id === activeMode)?.hint

  return (
    <Card>
      <CardHeader
        title="Add records"
        description="Three routes into the same read-only extraction engine. Records de-duplicate on ingest, so re-running a search is safe."
      />

      <div className="px-4">
        <Tabs
          items={enabledModes}
          value={activeMode}
          onChange={setMode}
          label="Ingestion method"
          idPrefix="ingest"
        />
      </div>

      <CardBody className="flex flex-col gap-4">
        {activeHint ? <p className="text-sm text-ink-muted">{activeHint}</p> : null}

        <TabPanel id="import" idPrefix="ingest" active={activeMode === 'import'}>
          <div className="flex flex-col gap-5">
            <FileImportPanel />

            {serverFiles.length > 0 ? (
              <div className="flex flex-col gap-2.5">
                <SectionHeader
                  level="eyebrow"
                  title="Or import from this server"
                  description={`${serverFiles.length} file${serverFiles.length === 1 ? '' : 's'} found in the configured import directory.`}
                />
                <ul className="flex flex-col gap-1.5">
                  {serverFiles.map((file) => {
                    const selected = selectedPaths.includes(file.path)
                    return (
                      <li key={file.path}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 transition-colors',
                            selected
                              ? 'border-accent bg-accent-soft'
                              : 'border-line hover:border-line-strong hover:bg-surface-hover',
                          )}
                        >
                          <Checkbox
                            checked={selected}
                            onChange={() => togglePath(file.path)}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-base text-ink">
                              {file.name}
                            </span>
                            <span className="block text-xs text-ink-muted">
                              {file.recordCount !== null
                                ? `${formatNumber(file.recordCount)} records · `
                                : ''}
                              {formatBytes(file.sizeBytes)} ·{' '}
                              {formatRelative(file.modifiedAt)}
                            </span>
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
                <Button
                  variant="secondary"
                  onClick={() => setConfirmServerImport(true)}
                  loading={jsonImport.isPending}
                  className="self-start"
                >
                  {selectedPaths.length === 0
                    ? 'Import all server files'
                    : `Import ${selectedPaths.length} selected`}
                </Button>
              </div>
            ) : null}
          </div>
        </TabPanel>

        <TabPanel id="curl" idPrefix="ingest" active={activeMode === 'curl'}>
          <div className="flex flex-col gap-4">
            <Alert tone="info" title="How to capture a session">
              Run the search in your own browser, then in DevTools → Network,
              right-click <code className="ident">judgmentSearchResult.faces</code> → Copy
              → <strong>Copy as cURL (bash)</strong>. Paging carries no captcha, so the
              tool walks every remaining page over plain HTTP. It does not matter which
              page you captured; it rewinds to page 1.
            </Alert>

            <Field
              label="Label"
              htmlFor="curl-label"
              hint="Names the capture in the audit trail."
            >
              <Input
                id="curl-label"
                value={curlLabel}
                onChange={(event) => setCurlLabel(event.target.value)}
                placeholder="e.g. smith-john-open"
              />
            </Field>

            <Field label="cURL command" htmlFor="curl-command" required>
              <Textarea
                id="curl-command"
                value={curlCommand}
                onChange={(event) => setCurlCommand(event.target.value)}
                rows={7}
                spellCheck={false}
                placeholder="curl 'https://portal.njcourts.gov/webe40/JudgmentWeb/jsp/judgmentSearchResult.faces' …"
                className="font-mono"
              />
            </Field>

            <Toggle
              id="curl-details"
              checked={fetchDetails}
              onChange={setFetchDetails}
              label="Read each detail page"
              description="Adds docket number, creditor and amount, one extra request per judgment."
            />

            <Button
              variant="primary"
              onClick={submitCurl}
              loading={curlCapture.isPending}
              disabled={curlCommand.trim().length < 20}
              className="self-start"
            >
              Start extraction
            </Button>
          </div>
        </TabPanel>

        <TabPanel id="browser" idPrefix="ingest" active={activeMode === 'browser'}>
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Last name"
                htmlFor="search-last"
                required
                hint="Required by the portal."
              >
                <Input
                  id="search-last"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  placeholder="e.g. SMITH"
                />
              </Field>
              <Field label="First name" htmlFor="search-first">
                <Input
                  id="search-first"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  placeholder="e.g. JOHN"
                />
              </Field>
              <Field label="Judgment status" htmlFor="search-status">
                <Select
                  id="search-status"
                  value={status}
                  options={STATUS_OPTIONS}
                  onChange={(event) => setStatus(event.target.value)}
                />
              </Field>
              <Field label="Party role" htmlFor="search-role">
                <Select
                  id="search-role"
                  value={partyRole}
                  options={ROLE_OPTIONS}
                  onChange={(event) => setPartyRole(event.target.value)}
                />
              </Field>
            </div>

            <Toggle
              id="browser-details"
              checked={fetchDetails}
              onChange={setFetchDetails}
              label="Read each detail page"
              description="Adds docket number, creditor and amount."
            />

            <Alert tone="warning" title="A common surname will not fit in one search">
              The portal displays at most 300 records and drops the rest silently.
              Split the search by status (the five statuses partition the set exactly)
              and run each part; records de-duplicate on ingest.
            </Alert>

            <Button
              variant="primary"
              onClick={submitBrowser}
              loading={browserSearch.isPending}
              disabled={lastName.trim().length === 0}
              className="self-start"
            >
              Start search
            </Button>
          </div>
        </TabPanel>
      </CardBody>

      <ConfirmDialog
        open={confirmServerImport}
        onCancel={() => setConfirmServerImport(false)}
        onConfirm={submitImport}
        title="Import into the shared database?"
        confirmLabel={`Import ${chosenCount} file${chosenCount === 1 ? '' : 's'}`}
        loading={jsonImport.isPending}
      >
        <p>
          {selectedPaths.length === 0
            ? `All ${serverFiles.length} file${serverFiles.length === 1 ? '' : 's'} in the server's import directory will be read.`
            : `${selectedPaths.length} selected file${selectedPaths.length === 1 ? '' : 's'} will be read.`}{' '}
          New judgments are created and matching ones are merged.
        </p>
        <p className="mt-2">
          Every service reads this same database, and no screen here can undo an
          import.
        </p>
      </ConfirmDialog>
    </Card>
  )
}
